<template>
  <div class="container text-black bg-white dark:bg-gray-900 dark:text-white">
    <StoreEmpty
      v-if="error"
      class="my-24"
      title="Oops! There was an error."
      desc="Either you hit the invalid URL or the product is not available anymore."
      icon="IconsNotoBrokenHeart"
      :action-label="$t('go_to_home')"
      :action-route="{ name: 'biz', params: { biz: subdomain } }"
    />

    <div v-else class="py-4 lg:py-12">
      <!-- Details -->
      <div class="lg:flex">
        <div class="lg:w-2/5">
          <ShimmerProductGallery v-if="loading" />
          <StoreProductGallery
            v-else
            class="sticky top-36"
            :items="data?.productBySlug?.media"
          />
        </div>
        <div class="px-4 py-4 space-y-6 lg:w-3/5 lg:ps-12 lg:pe-0">
          <ShimmerProductDetails v-if="loading" />
          <!-- product -->
          <template v-else>
            <!-- Badges -->
            <StoreProductBadges
              v-if="data?.productBySlug?.tags?.length > 0"
              :badges="data?.productBySlug?.tags"
              :product-id="parseInt(data?.productBySlug?.id)"
            />

            <!-- Name -->
            <section>
              <p class="text-2xl font-medium">
                {{ data?.productBySlug?.name }}
              </p>
            </section>

            <!-- Simple product-->
            <section
              v-if="
                data?.productBySlug?.type === 'simple' ||
                data?.productBySlug?.type === 'digital'
              "
              class="flex items-center justify-between py-4 border-t border-b"
            >
              <div>
                <StoreProductPrice
                  :type="data?.productBySlug?.type"
                  :regular="data?.productBySlug?.regularPrice"
                  :value="data?.productBySlug?.price"
                />
                <!-- Unit -->
                <p class="text-sm text-gray-500">
                  {{ data?.productBySlug?.measurementWithUnit }}
                </p>
              </div>

              <!-- Add To Cart: Simple product -->
              <div class="flex items-center gap-2 flex-nowrap">
                <StoreCartAddToCart
                  :id="parseInt(data?.productBySlug?.id)"
                  :max="data?.productBySlug?.stock"
                  type="simple"
                  :is-out-of-stock="data?.productBySlug?.isOutOfStock"
                  :manage-inventory="data?.productBySlug?.manageInventory"
                  :allow-qty="data?.productBySlug?.allowQty"
                  theme="solid"
                  size="lg"
                />
                <StoreBuyNow
                  :product-id="parseInt(data?.productBySlug?.id)"
                  :max="data?.productBySlug?.stock"
                  :is-out-of-stock="data?.productBySlug?.isOutOfStock"
                  :manage-inventory="data?.productBySlug?.manageInventory"
                />
              </div>
            </section>

            <!-- Variable product -->
            <section v-else-if="data?.productBySlug?.type === 'variable'">
              <StoreProductVariantInput
                class="pt-2 pb-4 border-t border-b"
                v-bind="{
                  attributes: data?.productBySlug?.attributes,
                  isOutOfStock: data?.productBySlug?.isOutOfStock,
                  price: data?.productBySlug?.price,
                  regularPrice: data?.productBySlug?.regularPrice,
                  status: data?.productBySlug?.status,
                  variants: data?.productBySlug?.variants,
                  media: data?.productBySlug?.media,
                }"
              >
                <template
                  #default="{
                    id,
                    stock,
                    isOutOfStock,
                    manageInventory,
                    notAvailable,
                    allowQty,
                  }"
                >
                  <div
                    class="grid items-center justify-between grid-cols-2 gap-2 lg:flex lg:flex-row-reverse lg:justify-end"
                  >
                    <StoreBuyNow
                      :product-id="id"
                      :is-out-of-stock="isOutOfStock"
                      :not-available="notAvailable"
                      :allow-qty="allowQty"
                      :max="stock"
                      :manage-inventory="manageInventory"
                    />
                    <StoreCartAddToCart
                      :id="id"
                      :allow-qty="allowQty"
                      :is-out-of-stock="isOutOfStock"
                      :manage-inventory="manageInventory"
                      :max="stock"
                      type="variant"
                      :not-available="notAvailable"
                      button-class="w-full lg:w-auto"
                      qty-class="ms-auto lg:ms-0"
                      theme="solid"
                      size="lg"
                    />
                  </div>
                </template>
              </StoreProductVariantInput>
            </section>

            <!--Custom fields-->
            <section v-if="data?.productBySlug?.customFields">
              <table class="w-full">
                <tbody>
                  <tr
                    v-for="(field, key) in filteredCustomFields"
                    :key="key"
                    class="w-full border-b"
                  >
                    <td class="w-1/3">
                      <span class="font-bold">{{ field.name }}</span>
                    </td>
                    <td class="ps-4">
                      {{
                        field.selectedOptions.length > 0
                          ? field.selectedOptions[0].label
                          : field.value
                      }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </section>

            <!-- Description -->
            <section v-if="data?.productBySlug?.desc">
              <p class="break-words whitespace-pre-line">
                {{ data?.productBySlug?.desc }}
              </p>
            </section>

            <!-- Contents: Digital data -->
            <section
              v-if="
                data?.productBySlug?.contents &&
                data?.productBySlug?.contents.length > 0
              "
              class="pt-4 border-t"
            >
              <p class="text-lg font-semibold">Contents</p>
              <StoreProductContents
                class="mt-3"
                :items="data?.productBySlug?.contents"
              />
            </section>

            <!-- More URL -->
            <section v-if="data?.productBySlug?.moreUrl">
              <VyButton
                size="lg"
                color="primary"
                shape="rounded"
                theme="muted"
                icon="MdiOpenInNew"
                :label="$t('more_details')"
                direction="rowReverse"
                :href="data?.productBySlug?.moreUrl"
                target="_blank"
              />
            </section>
          </template>
        </div>
      </div>

      <!-- Overview -->

      <!-- <div v-if="productOverview" class="prose">
          <MDCRenderer
            :body="productOverview.body"
            :data="productOverview.data"
          />
        </div> -->

      <!-- Ratings & Reviews -->
      <div v-if="!loading" class="px-4 pt-8 mt-8 border-t sm:px-0">
        <h4 class="text-xl font-bold font-display">
          {{ $t("rating_and_reviews") }}
        </h4>

        <div
          v-if="!loading && useProductStore()?.product?.id"
          class="mt-4 lg:flex"
        >
          <div class="lg:w-2/5 lg:flex-shrink-0">
            <StoreProductRating class="sticky top-36" />
          </div>
          <div class="flex-grow mt-8 lg:mt-0 lg:ps-12">
            <StoreCta
              v-if="!user.id"
              :title="$t('purchased_this_product')"
              :desc="$t('login_drop_your_valuable_review')"
              class="p-6 mt-8 mb-4 border rounded-lg lg:mt-0"
              button-icon="MdiStar"
              :button-label="$t('leave_review')"
              @click="
                $nuxt.callHook(
                  'modal-open',
                  'modal-login',
                  `${route.params.biz}/products/${route.params.slug}`
                )
              "
            />
            <StoreProductReviews />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRoute, useAsyncData, useHead, useNuxtApp } from "nuxt/app";
import { ref, computed } from "vue";
import { useVepaarStore } from "@/store/store.js";
import { useBizStore } from "@/store/index.js";
import { useProductStore } from "@/store/product.js";
import { useAuthStore } from "@/store/auth";
import productQuery from "@/graph/store/product.gql";
// import { parseMarkdown } from "@nuxtjs/mdc/runtime";

definePageMeta({
  layout: "store",
  scrollToTop: true,
});

const nuxtApp = useNuxtApp();
const hash = useVepaarStore().hash;
const route = useRoute();
const url = useBizStore().url;
const logo = useBizStore().logo;
const subdomain = useBizStore().subdomain;

const product = ref(null);
const loading = ref(true);
const error = ref(null);
const user = useAuthStore().user;
const { clients } = useApollo();
// const productOverview = ref(null);
const { data } = await useAsyncData("product", async () => {
  try {
    const response = await clients.default.query({
      query: productQuery,
      variables: {
        slug: route.params.slug,
        hash: hash,
      },
    });

    nuxtApp.runWithContext(() => {
      useProductStore().set(response?.data?.productBySlug);
    });

    // if (response.data.productBySlug.overview) {
    //   productOverview.value = await parseMarkdown(
    //     response.data.productBySlug.overview
    //   );
    // }

    return response?.data;
  } catch (err) {
    error.value = err;
    console.error("Error fetching product data:", err);
    return { productBySlug: null };
  }
});

if (data) {
  product.value = data.value?.productBySlug;
  loading.value = false;
}

const filteredCustomFields = computed(() => {
  return product.value?.customFields?.filter((field) => field.value) ?? [];
});

const imageContent =
  product.value?.media?.find((item) =>
    (item.mimetype || "image").startsWith("image")
  )?.size?._500x500 ??
  logo?.value ??
  "";

useHead({
  title: product.value?.name,
  meta: [
    {
      hid: "og:title",
      property: "og:title",
      content: product.value?.name,
    },
    {
      hid: "og:type",
      property: "og:type",
      content: "product",
    },
    {
      hid: "og:url",
      property: "og:url",
      content: `${url.value}/products/${route.params.slug}`,
    },
    {
      hid: "og:image",
      property: "og:image",
      content: imageContent,
    },
  ],
});
</script>
